<template>
  <div
    class="testimony-carousel"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
  >
    <div class="carousel-container">
      <div v-if="testimonies.length > 0" class="testimonies-wrapper">
        <!-- Navegação anterior -->
        <button
          class="nav-button prev"
          @click="previousTestimony"
          aria-label="Anterior"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <!-- Conteúdo do depoimento -->
        <div class="testimony-content">
          <div class="testimony-card">
            <div class="testimony-header">
              <div class="testimony-quote">
                <i class="fas fa-quote-left"></i>
              </div>
              <div class="testimony-info">
                <h3>{{ currentTestimony.name }}</h3>
                <p>{{ currentTestimony.description }}</p>
                <div class="rating">
                  <i class="fas fa-star" v-for="n in 5" :key="n"></i>
                </div>
              </div>
            </div>

            <div class="video-wrapper">
              <!-- Vídeo do YouTube -->
              <iframe
                v-if="currentTestimony.video_type === 'youtube'"
                :src="
                  'https://www.youtube.com/embed/' + currentTestimony.video_id
                "
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="video-player"
                @play="handlePlay"
              ></iframe>
              <!-- Vídeo enviado -->
              <video
                v-else-if="currentTestimony.video_type === 'upload'"
                ref="videoPlayer"
                :src="currentTestimony.video_url"
                controls
                @play="handlePlay"
                @ended="handleEnded"
                class="video-player"
              >
                <source :src="currentTestimony.video_url" type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </video>
            </div>
          </div>
        </div>

        <!-- Navegação próximo -->
        <button
          class="nav-button next"
          @click="nextTestimony"
          aria-label="Próximo"
        >
          <i class="fas fa-chevron-right"></i>
        </button>

        <!-- Indicadores -->
        <div class="testimony-indicators">
          <button
            v-for="(testimony, index) in testimonies"
            :key="testimony.id"
            :class="['indicator', { active: index === currentIndex }]"
            @click="goToTestimony(index)"
            :aria-label="'Ir para depoimento ' + (index + 1)"
          ></button>
        </div>
      </div>
      <div v-else class="no-testimonies">
        <p>Nenhum depoimento disponível</p>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "@/root/firebase";

export default {
  name: "TestimonyCarousel",
  data() {
    return {
      testimonies: [],
      currentIndex: 0,
      autoplayInterval: null,
      isPlaying: false,
    };
  },
  computed: {
    currentTestimony() {
      return this.testimonies[this.currentIndex] || {};
    },
  },
  async created() {
    await this.fetchTestimonies();
    this.startAutoplay();
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    async fetchTestimonies() {
      try {
        const q = query(
          collection(db, "testimonies"),
          orderBy("created_at", "desc")
        );
        const querySnapshot = await getDocs(q);
        this.testimonies = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Erro ao buscar depoimentos:", error);
      }
    },
    startAutoplay() {
      if (!this.isPlaying) {
        this.autoplayInterval = setInterval(() => {
          if (this.testimonies.length > 0) {
            this.nextTestimony();
          }
        }, 10000); // Muda a cada 10 segundos
      }
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    },
    handlePlay() {
      this.isPlaying = true;
      this.stopAutoplay();
    },
    handleEnded() {
      this.isPlaying = false;
      this.startAutoplay();
    },
    nextTestimony() {
      this.currentIndex = (this.currentIndex + 1) % this.testimonies.length;
    },
    previousTestimony() {
      this.currentIndex =
        this.currentIndex === 0
          ? this.testimonies.length - 1
          : this.currentIndex - 1;
    },
    goToTestimony(index) {
      this.currentIndex = index;
    },
  },
};
</script>

<style scoped>
.testimony-carousel {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 0;
  position: relative;
}

.carousel-container {
  background-color: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.testimonies-wrapper {
  position: relative;
  padding: 0 40px;
}

.testimony-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.testimony-header {
  display: flex;
  align-items: flex-start;
  padding: 15px 20px;
  background: #f8f9fa;
}

.testimony-quote {
  font-size: 20px;
  color: #e74c3c;
  margin-right: 15px;
}

.testimony-info {
  flex: 1;
}

.testimony-info h3 {
  margin: 0 0 5px;
  font-size: 18px;
  color: #2c3e50;
}

.testimony-info p {
  margin: 0 0 8px;
  color: #666;
  line-height: 1.4;
  font-size: 14px;
}

.rating {
  color: #f1c40f;
  font-size: 12px;
}

.rating i {
  margin-right: 2px;
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 40%; /* Reduzindo a altura do vídeo */
}

.video-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0 0 8px 8px;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 2;
}

.nav-button:hover {
  background: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.nav-button.prev {
  left: 5px;
}

.nav-button.next {
  right: 5px;
}

.testimony-indicators {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 15px;
}

.indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ddd;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: #e74c3c;
  transform: scale(1.2);
}

.no-testimonies {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 14px;
}

@media (max-width: 768px) {
  .testimony-carousel {
    padding: 10px;
  }

  .carousel-container {
    padding: 10px;
  }

  .testimony-header {
    padding: 10px;
    flex-direction: column;
  }

  .testimony-quote {
    margin-bottom: 5px;
  }

  .nav-button {
    width: 28px;
    height: 28px;
  }

  .video-wrapper {
    padding-top: 50%; /* Ajuste para mobile */
  }
}
</style>
